import React, { useState } from 'react';
import { Form, Input, Button, Card, Switch, Select, message, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { LinkOutlined, RobotOutlined } from '@ant-design/icons';
import MDEditor from '@uiw/react-md-editor';
import { useNoteStore } from '../stores/noteStore';

const { Option } = Select;
const { TextArea } = Input;

const CreateNote = () => {
  const navigate = useNavigate();
  const { createNote, extractFromUrl, generateSummary, isLoading } = useNoteStore();
  
  const [form] = Form.useForm();
  const [content, setContent] = useState('');
  const [urlModalVisible, setUrlModalVisible] = useState(false);
  const [extractUrl, setExtractUrl] = useState('');
  
  const handleExtractFromUrl = async () => {
    if (!extractUrl) {
      message.error('Please enter a URL');
      return;
    }
    
    const result = await extractFromUrl(extractUrl);
    if (result.success) {
      form.setFieldsValue({
        title: result.data.title,
        source_url: extractUrl,
        source_type: 'web',
        summary: result.data.summary,
      });
      setContent(result.data.content);
      message.success('Content extracted successfully');
    } else {
      message.error(result.message);
    }
  };

  const handleGenerateSummary = async () => {
    if (!content) {
      message.error('Please enter note content first');
      return;
    }

    const result = await generateSummary(content);
    if (result.success) {
      form.setFieldsValue({
        summary: result.data.summary,
      });
      message.success('Summary generated successfully');
    } else {
      message.error(result.message);
    }
  };
  
  const onFinish = async (values) => {
    const noteData = {
      ...values,
      content,
    };
    
    const result = await createNote(noteData);
    if (result.success) {
      message.success('Note created successfully');
      navigate(`/notes/${result.data.id}`);
    } else {
      message.error(result.message);
    }
  };
  
  return (
    <div>
      <h2>Create Note</h2>
      
      <Card style={{ marginBottom: 16 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <span>Extract content from URL</span>
          <Space>
            <Input 
              placeholder="Enter URL" 
              value={extractUrl} 
              onChange={(e) => setExtractUrl(e.target.value)}
              style={{ width: 300 }}
              prefix={<LinkOutlined />}
            />
            <Button 
              type="primary" 
              onClick={handleExtractFromUrl}
              loading={isLoading}
            >
              Extract
            </Button>
          </Space>
        </Space>
      </Card>
      
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          is_public: true,
        }}
      >
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: 'Please enter a note title!' }]}
        >
          <Input placeholder="Enter note title" />
        </Form.Item>
        
        <Form.Item label="Content">
          <MDEditor
            value={content}
            onChange={setContent}
            height={400}
          />
        </Form.Item>

        <Form.Item
          name="summary"
          label={
            <Space>
              Summary
              <Button
                type="link"
                icon={<RobotOutlined />}
                onClick={handleGenerateSummary}
                loading={isLoading}
              >
                Auto-generate summary
              </Button>
            </Space>
          }
        >
          <TextArea
            placeholder="Enter note summary, or click the button to auto-generate"
            autoSize={{ minRows: 2, maxRows: 6 }}
          />
        </Form.Item>
        
        <Form.Item
          name="tags"
          label="Tags"
        >
          <Select mode="tags" placeholder="Add tags">
            <Option value="Work">Work</Option>
            <Option value="Study">Study</Option>
            <Option value="Life">Life</Option>
          </Select>
        </Form.Item>
        
        <Form.Item
          name="source_url"
          label="Source URL"
        >
          <Input placeholder="Content source URL" />
        </Form.Item>
        
        <Form.Item
          name="source_type"
          label="Content Type"
        >
          <Select placeholder="Select content type">
            <Option value="web">Web Page</Option>
            <Option value="pdf">PDF</Option>
            <Option value="image">Image</Option>
            <Option value="text">Text</Option>
          </Select>
        </Form.Item>
        
        <Form.Item
          name="is_public"
          label="Public"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              Create Note
            </Button>
            <Button onClick={() => navigate('/notes')}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default CreateNote;
